import {
  RefreshCcw,
  Download,
  ChevronUp,
  Shuffle,
  Camera,
  PanelRightClose,
  Upload,
  Loader2,
  Check,
  Clipboard,
  House,
  LockKeyhole,
  LockKeyholeOpen,
  ChevronDown,
  SlidersHorizontal,
  ExternalLink,
  FileJson,
  FolderDown,
} from 'lucide-react';
import {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  useImperativeHandle,
} from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Parameter } from '@shared/types';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ParameterInput } from '@/components/parameter/ParameterInput';
import { ModelColorSection } from '@/components/parameter/ModelColorSection';
import {
  validateParameterValue,
  serializeSnapshotLine,
  resolveParameter,
  resolveRange,
  calculateParameterRange,
  type CodeSnapshot,
} from '@/utils/parameterUtils';
import { evaluateVisibility, buildValueMap } from '@/lib/visibilityExpr';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { randomizeParameters } from '@/utils/randomizeUtils';
import { useParameterSnapshots } from '@/hooks/useParameterSnapshots';
import { downloadSTLFile, downloadOpenSCADFile, downloadParametersAsJson, buildParametersJson } from '@/utils/downloadUtils';
import { useBlob } from '@/contexts/BlobContext';
import type { ProjectFile } from '@/components/files/FileManager';
import { useHighQualityExport } from '@/hooks/useHighQualityExport';
import {
  type ExportQuality,
  EXPORT_QUALITY_LABELS,
  EXPORT_QUALITY_DESCRIPTIONS,
} from '@/utils/exportQualityUtils';

const EMPTY_PROJECT_FILES: ProjectFile[] = [];

export interface ParameterSectionProps {
  parameters: Parameter[];
  onParametersChange: (params: Parameter[]) => void;
  scadCode?: string;
  projectFiles?: ProjectFile[];
  onFileAdd?: (file: ProjectFile) => void;
  onCollapse?: () => void;
  /** When true, renders in overlay-friendly style without header/download sections */
  productMode?: boolean;
  /** Ref that exposes imperative actions to parent (command palette) */
  actionRef?: React.MutableRefObject<ParameterSectionActions | null>;
  /** Pre-defined named snapshots parsed from the SCAD source */
  codeSnapshots?: CodeSnapshot[];
  /**
   * Called when the user hovers over a parameter row.
   * Passes the parameter name on mouse-enter, or null on mouse-leave.
   * Used by the code editor's colour indicator bar to highlight the
   * corresponding declaration line.
   */
  onParamHover?: (paramName: string | null) => void;
  /**
   * Ref to the pub/sub publish function. When set, changes to @pubsub
   * parameters are published to the pub/sub channel (key = variable name).
   * Populated asynchronously after the 3D scene mounts.
   */
  pubSubPublishRef?: React.RefObject<
    ((key: string, value: unknown) => void) | null
  >;
  /**
   * Ref to the pub/sub subscribe function. When set, the ParameterSection
   * subscribes to incoming messages for each @pubsub parameter key so that
   * script-driven changes update the UI in real time.
   * Populated asynchronously after the 3D scene mounts.
   */
  pubSubSubscribeRef?: React.RefObject<
    | ((key: string, callback: (value: unknown) => void) => () => void)
    | null
  >;
}

export interface ParameterSectionActions {
  openCreateSnapshotDialog: () => void;
  triggerImportParameters: () => void;
}

export function ParameterSection({
  parameters,
  onParametersChange,
  scadCode,
  projectFiles = EMPTY_PROJECT_FILES,
  onFileAdd,
  onCollapse,
  productMode = false,
  actionRef,
  codeSnapshots = [],
  onParamHover,
  pubSubPublishRef,
  pubSubSubscribeRef,
}: ParameterSectionProps) {
  const { blob, coloredBlobs } = useBlob();
  const [selectedFormat, setSelectedFormat] = useState<'stl' | 'scad'>('stl');
  const [exportQuality, setExportQuality] = useState<ExportQuality>(1);
  const [showSnapshotDialog, setShowSnapshotDialog] = useState(false);

  // Randomizer state
  const [lockedParams, setLockedParams] = useState<Set<string>>(new Set());
  const [randomStrength, setRandomStrength] = useState(1);
  // Tracks params that the user has explicitly unlocked (overrides @lock annotation).
  // When a param with locked:true appears for the first time it gets added to
  // lockedParams automatically — unless the user has previously unlocked it.
  const userUnlockedParams = useRef<Set<string>>(new Set());
  // Tracks the set of param names seen so far, so we only auto-lock on first appearance.
  const seenParamNames = useRef<Set<string>>(new Set());

  // ── @pubsub parameter state ──────────────────────────────────────────────
  // Local override map for @pubsub parameter values received from the pub/sub
  // channel. These values shadow the values from the `parameters` prop so that
  // script-driven updates are reflected in the UI immediately without a
  // recompile (which would clear them).
  const [pubSubOverrides, setPubSubOverrides] = useState<
    Map<string, Parameter['value']>
  >(new Map());

  // On mount (and whenever the subscribe ref or @pubsub params change), subscribe
  // to incoming pub/sub messages for each @pubsub parameter. Unsubscribes on
  // unmount or when dependencies change.
  const pubsubParams = useMemo(
    () => parameters.filter((p) => p.pubsub),
    [parameters],
  );

  useEffect(() => {
    const subscribe = pubSubSubscribeRef?.current;
    if (!subscribe || pubsubParams.length === 0) return;

    const unsubscribers: (() => void)[] = [];

    for (const param of pubsubParams) {
      const unsub = subscribe(param.name, (rawValue) => {
        // Coerce the incoming value to match the parameter type as best we can.
        let coerced: Parameter['value'] = rawValue as Parameter['value'];
        if (
          param.type === 'number' ||
          param.type === 'angle' ||
          param.type === undefined
        ) {
          const n = Number(rawValue);
          if (!isNaN(n)) coerced = n;
        } else if (param.type === 'boolean') {
          coerced = Boolean(rawValue);
        } else if (param.type === 'string') {
          coerced = String(rawValue);
        }
        // For arrays and other complex types pass the raw value through — the
        // script is expected to publish in the correct format.
        setPubSubOverrides((prev) => {
          const next = new Map(prev);
          next.set(param.name, coerced);
          return next;
        });
      });
      unsubscribers.push(unsub);
    }

    return () => {
      for (const unsub of unsubscribers) unsub();
    };
  // Re-subscribe whenever the subscribe function or the set of @pubsub params changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pubSubSubscribeRef, pubsubParams]);

  // Publish initial values for all @pubsub params once the publish function is ready.
  // Also re-publish whenever the parameters list changes (e.g. default value updated).
  useEffect(() => {
    const publish = pubSubPublishRef?.current;
    if (!publish || pubsubParams.length === 0) return;

    for (const param of pubsubParams) {
      // Use the override value if we have one, otherwise the parsed default.
      const currentValue = pubSubOverrides.get(param.name) ?? param.value;
      publish(param.name, currentValue);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pubSubPublishRef, pubsubParams]);

  // Merge @pubsub overrides into the parameters array for rendering.
  // This is a derived value — no state update needed since pubSubOverrides
  // drives the re-render independently.
  const effectiveParameters = useMemo(() => {
    if (pubSubOverrides.size === 0) return parameters;
    return parameters.map((p) => {
      if (!p.pubsub) return p;
      const override = pubSubOverrides.get(p.name);
      if (override === undefined) return p;
      return { ...p, value: override };
    });
  }, [parameters, pubSubOverrides]);

  // Proportional scale state
  // Slider position 0–100 where 50 = 1× (no change)
  const [scaleSlider, setScaleSlider] = useState(50);
  const [scalePopoverOpen, setScalePopoverOpen] = useState(false);
  // Baseline values snapshotted when the popover opens
  const scaleBaselineRef = useRef<Map<string, number>>(new Map());

  const { compileForExport, isExporting, exportProgress } =
    useHighQualityExport();
  const [snapshotName, setSnapshotName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Expose imperative actions to parent (for command palette)
  useImperativeHandle(actionRef, () => ({
    openCreateSnapshotDialog: () => {
      setSnapshotName(new Date().toLocaleString());
      setShowSnapshotDialog(true);
    },
    triggerImportParameters: () => {
      fileInputRef.current?.click();
    },
  }));

  const { snapshots, createSnapshot, applySnapshot } = useParameterSnapshots(
    parameters,
    codeSnapshots,
  );

  // Seed lockedParams from @lock annotations for parameters appearing for the
  // first time. Parameters the user has explicitly unlocked are never re-locked.
  useEffect(() => {
    const newlyLocked: string[] = [];
    for (const param of parameters) {
      if (
        param.locked &&
        !seenParamNames.current.has(param.name) &&
        !userUnlockedParams.current.has(param.name)
      ) {
        newlyLocked.push(param.name);
      }
      seenParamNames.current.add(param.name);
    }
    if (newlyLocked.length > 0) {
      setLockedParams((prev) => {
        const next = new Set(prev);
        for (const name of newlyLocked) next.add(name);
        return next;
      });
    }
  }, [parameters]);

  // Lock handlers
  const handleToggleLock = useCallback((paramName: string) => {
    setLockedParams((prev) => {
      const next = new Set(prev);
      if (next.has(paramName)) {
        next.delete(paramName);
        // User manually unlocked — remember so @lock doesn't re-apply
        userUnlockedParams.current.add(paramName);
      } else {
        next.add(paramName);
        // User manually re-locked — clear the unlock override
        userUnlockedParams.current.delete(paramName);
      }
      return next;
    });
  }, []);

  const handleLockAll = useCallback(() => {
    setLockedParams(new Set(parameters.map((p) => p.name)));
    // Locking all clears all unlock overrides
    userUnlockedParams.current.clear();
  }, [parameters]);

  const handleUnlockAll = useCallback(() => {
    setLockedParams(new Set());
    // Mark all currently @lock params as user-unlocked so they stay unlocked
    // through subsequent re-parses (user's intent takes precedence).
    for (const param of parameters) {
      if (param.locked) {
        userUnlockedParams.current.add(param.name);
      }
    }
  }, [parameters]);

  // Debounce timer for compilation
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingParametersRef = useRef<Parameter[] | null>(null);

  // Group parameters, with optional tab-level bucketing.
  //
  // When any parameter carries a `tab` value, the UI renders named tabs.
  // The home tab (value = '__home__') holds ungrouped params and any groups
  // that have no tab prefix (plain `/* [Group Name] */`).
  // Named tabs hold groups declared with `/* [Tab Name: Group Name] */`.
  const groupedParameters = useMemo(() => {
    type TabEntry = {
      groups: { [key: string]: Parameter[] };
      collapsedGroups: Set<string>;
      ungrouped: Parameter[];
    };

    const HOME_TAB = '__home__';
    const tabMap: { [tabKey: string]: TabEntry } = {};
    const tabOrder: string[] = []; // preserves insertion order

    const getOrCreateTab = (key: string): TabEntry => {
      if (!tabMap[key]) {
        tabMap[key] = { groups: {}, collapsedGroups: new Set(), ungrouped: [] };
        tabOrder.push(key);
      }
      return tabMap[key];
    };

    // Ensure the home tab is always first
    getOrCreateTab(HOME_TAB);

    effectiveParameters.forEach((param) => {
      const tabKey = param.tab ?? HOME_TAB;
      const entry = getOrCreateTab(tabKey);

      if (param.group && param.group !== 'General') {
        // Strip trailing ^ from the display name; track collapsed groups
        const rawGroup = param.group;
        const isCollapsed = rawGroup.endsWith('^');
        const cleanGroup = isCollapsed
          ? rawGroup.slice(0, -1).trimEnd()
          : rawGroup;

        if (isCollapsed) entry.collapsedGroups.add(cleanGroup);

        if (!entry.groups[cleanGroup]) {
          entry.groups[cleanGroup] = [];
        }
        // Store param with the clean group name so AccordionItem values match
        entry.groups[cleanGroup].push({ ...param, group: cleanGroup });
      } else {
        entry.ungrouped.push(param);
      }
    });

    // hasTabs is true when at least one parameter has a named tab
    const hasTabs = effectiveParameters.some((p) => p.tab !== undefined);

    return { tabMap, tabOrder, hasTabs, HOME_TAB };
  }, [effectiveParameters]);

  // Flat map of param name → current value, used for visibility expression evaluation
  const paramValueMap = useMemo(
    () => buildValueMap(effectiveParameters),
    [effectiveParameters],
  );

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Debounced submit function
  const debouncedSubmit = useCallback(
    (params: Parameter[]) => {
      // Store the parameters to submit
      pendingParametersRef.current = params;

      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new debounced timer (200ms delay)
      debounceTimerRef.current = setTimeout(() => {
        if (pendingParametersRef.current) {
          onParametersChange(pendingParametersRef.current);
          pendingParametersRef.current = null;
        }
      }, 200);
    },
    [onParametersChange],
  );

  // ── Proportional scale ──────────────────────────────────────────────────────

  /** Maps a linear slider position (0–100) to a scale multiplier.
   *  0 → 0.1×,  50 → 1× (no change),  100 → 3×
   *  Uses two linear segments so the center is always exactly 1×.
   */
  const sliderToFactor = (v: number): number => {
    if (v <= 50) return 0.1 + (v / 50) * 0.9; // 0→0.1, 50→1.0
    return 1 + ((v - 50) / 50) * 2; // 50→1, 100→3
  };

  /** Snapshot current unlocked number-param values as the scaling baseline. */
  const captureScaleBaseline = useCallback(() => {
    const map = new Map<string, number>();
    effectiveParameters.forEach((p) => {
      // @pubsub params are excluded from scaling — they don't drive geometry
      if ((!p.type || p.type === 'number') && !lockedParams.has(p.name) && !p.pubsub) {
        map.set(
          p.name,
          typeof p.value === 'number' ? p.value : Number(p.value),
        );
      }
    });
    scaleBaselineRef.current = map;
  }, [effectiveParameters, lockedParams]);

  /** Apply the current scale factor to all eligible params and submit. */
  const handleScaleChange = useCallback(
    (sliderValue: number) => {
      setScaleSlider(sliderValue);
      const factor = sliderToFactor(sliderValue);
      const baseline = scaleBaselineRef.current;

      const newParams = parameters.map((param) => {
        if (
          (param.type && param.type !== 'number') ||
          lockedParams.has(param.name) ||
          param.pubsub // @pubsub params are not geometry-driven, skip scaling
        ) {
          return param;
        }
        const base = baseline.get(param.name);
        // Skip params with no baseline or a zero base value
        if (base === undefined || base === 0) return param;

        const { min, max } = calculateParameterRange(param);
        const step =
          typeof param.range?.step === 'number' ? param.range.step : undefined;
        let newVal = Math.min(max, Math.max(min, base * factor));
        if (step) {
          newVal = min + Math.round((newVal - min) / step) * step;
          // Re-clamp after step rounding
          newVal = Math.min(max, Math.max(min, newVal));
        } else {
          newVal = Math.round(newVal * 100) / 100;
        }
        return { ...param, value: newVal };
      });

      debouncedSubmit(newParams);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parameters, lockedParams, debouncedSubmit],
  );

  /** True when there is at least one unlocked numeric param with a non-zero value
   *  that could actually be scaled. */
  const hasScalableParams = useMemo(
    () =>
      effectiveParameters.some(
        (p) =>
          (!p.type || p.type === 'number') &&
          !lockedParams.has(p.name) &&
          !p.pubsub &&
          (typeof p.value === 'number' ? p.value : Number(p.value)) !== 0,
      ),
    [effectiveParameters, lockedParams],
  );

  // ── Parameter commit ────────────────────────────────────────────────────────

  const handleCommit = (param: Parameter, value: Parameter['value']) => {
    const validatedValue = validateParameterValue(param, value);

    // @pubsub parameters: publish to the pub/sub channel and update local
    // override state, but do NOT trigger a SCAD recompile (the variable is
    // stripped from the source so there's nothing to recompile).
    if (param.pubsub) {
      const publish = pubSubPublishRef?.current;
      if (publish) {
        publish(param.name, validatedValue);
      }
      // Also update local override so the UI reflects the new value immediately
      // even before the subscriber callback fires.
      setPubSubOverrides((prev) => {
        const next = new Map(prev);
        next.set(param.name, validatedValue);
        return next;
      });
      return; // Skip the normal compile-triggering path
    }

    const updatedParam = { ...param, value: validatedValue };
    const updatedParameters = parameters.map((p) =>
      p.name === param.name ? updatedParam : p,
    );

    // Clamp any numeric parameter whose dynamic bounds have shifted due to this change.
    // Build a value map from the just-updated list so $-references resolve correctly.
    const newValueMap = buildValueMap(updatedParameters);
    const clampedParameters = updatedParameters.map((p) => {
      if (
        (p.type !== 'number' && p.type !== 'angle' && p.type !== undefined) ||
        p.name === param.name
      )
        return p;
      if (typeof p.value !== 'number') return p;
      const resolvedRange = resolveRange(p.range, newValueMap);
      const { min, max } = calculateParameterRange({
        ...p,
        range: resolvedRange,
      });
      if (p.value < min) return { ...p, value: min };
      if (p.value > max) return { ...p, value: max };
      return p;
    });

    debouncedSubmit(clampedParameters);
  };

  const handleDownload = async () => {
    if (selectedFormat === 'stl') {
      await handleDownloadSTL();
    } else {
      handleDownloadOpenSCAD();
    }
  };

  const handleDownloadSTL = async () => {
    if (!blob) return;

    if (exportQuality === 1) {
      await downloadSTLFile(blob, undefined, coloredBlobs, parameters);
      return;
    }

    // Higher quality: re-compile with scaled $fn via a dedicated worker
    const workerFiles = (projectFiles ?? []).map((f) => ({
      filename: f.filename,
      content: f.content,
      isBinary: f.isBinary,
    }));
    const result = await compileForExport(
      scadCode ?? '',
      workerFiles,
      exportQuality,
    );
    if (result) {
      await downloadSTLFile(
        result.blob,
        undefined,
        result.coloredBlobs,
        parameters,
      );
    }
  };

  const handleDownloadOpenSCAD = () => {
    if (!scadCode) return;
    downloadOpenSCADFile(scadCode, undefined);
  };

  const handleCreateSnapshot = () => {
    setSnapshotName(new Date().toLocaleString());
    setShowSnapshotDialog(true);
  };

  const confirmCreateSnapshot = () => {
    createSnapshot(snapshotName);
    setShowSnapshotDialog(false);
  };

  const handleApplySnapshot = (snapshotId: string) => {
    // Markdown action links emit the bare snapshot name (e.g. "compact") while
    // code-defined snapshots are stored with a "code::" prefix ("code::compact").
    // Try the raw ID first, then fall back to the prefixed form so both the
    // snapshot dropdown (which passes the full ID) and markdown links work.
    const newParams =
      applySnapshot(snapshotId) ?? applySnapshot(`code::${snapshotId}`);
    if (newParams) {
      onParametersChange(newParams);
    }
  };

  /**
   * Handles a `meshpit:<paramName>:<value>` action link click.
   * Only applies to parameters that have a defined options list; all other
   * parameter types are silently ignored to prevent unexpected mutations.
   */
  const handleSetParameter = (name: string, value: string) => {
    const param = parameters.find((p) => p.name === name);
    if (!param) return;

    // If the parameter has an options list, match by value or label first.
    if (param.options && param.options.length > 0) {
      const matchedOption = param.options.find(
        (opt) => String(opt.value) === value || opt.label === value,
      );
      if (matchedOption) {
        handleCommit(param, matchedOption.value);
        return;
      }
    }

    // For numeric parameters, coerce the string to a number.
    if (param.type === 'number' || param.type === 'angle') {
      const n = Number(value);
      if (!isNaN(n)) {
        handleCommit(param, n);
        return;
      }
    }

    // For boolean parameters, accept "true" / "false".
    if (param.type === 'boolean') {
      if (value === 'true' || value === 'false') {
        handleCommit(param, value === 'true');
        return;
      }
    }

    // For string / font / file / color-string parameters, set directly.
    if (
      param.type === 'string' ||
      param.type === 'font' ||
      param.type === 'file' ||
      param.type === 'color'
    ) {
      handleCommit(param, value);
      return;
    }
  };

  const handleCopyAsSnapshot = () => {
    const name = new Date().toLocaleString();
    const line = serializeSnapshotLine(name, parameters);
    navigator.clipboard.writeText(line).catch(console.error);
  };

  const handleDownloadSnapshotJson = () => {
    downloadParametersAsJson(parameters, 'parameters.json');
  };

  const handleAddSnapshotToFiles = () => {
    if (!onFileAdd) return;
    const content = buildParametersJson(parameters);
    // Find a non-conflicting filename
    const existingNames = new Set(
      (projectFiles ?? []).map((f) => f.filename),
    );
    let filename = 'parameters.json';
    if (existingNames.has(filename)) {
      let i = 2;
      while (existingNames.has(`parameters_${i}.json`)) i++;
      filename = `parameters_${i}.json`;
    }
    const file: ProjectFile = {
      id: crypto.randomUUID(),
      filename,
      fileType: 'json',
      content,
      size: new TextEncoder().encode(content).length,
      isModified: false,
      isBinary: false,
    };
    onFileAdd(file);
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportParameters = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const importedParams = JSON.parse(content);

        if (typeof importedParams !== 'object' || importedParams === null) {
          console.error('Invalid JSON: Root must be an object');
          return;
        }

        // Merge imported values into existing parameters
        const updatedParameters = parameters.map((param) => {
          if (param.name in importedParams) {
            let newValue = importedParams[param.name];
            // Basic validation: Check if type matches default value type if possible
            // Ideally we'd use more robust validation, but this prevents some obvious errors
            // We rely on updateParameter and validateParameterValue to handle some coercion
            return {
              ...param,
              value: validateParameterValue(param, newValue),
            };
          }
          return param;
        });

        onParametersChange(updatedParameters);
      } catch (error) {
        console.error('Failed to import parameters:', error);
        // User notification could be added here (e.g. toast)
      } finally {
        // Reset input so same file can be selected again
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };
    reader.readAsText(file);
  };

  const isDownloadDisabled =
    (selectedFormat === 'stl' ? !blob : !scadCode) || isExporting;

  // ---------------------------------------------------------------------------
  // Shared render helpers
  // ---------------------------------------------------------------------------

  /** Renders the ungrouped params + accordion groups for a single tab entry. */
  const renderTabContent = (
    tabKey: string,
    padding: 'compact' | 'normal' = 'normal',
  ) => {
    const entry = groupedParameters.tabMap[tabKey];
    if (!entry) return null;

    const hasParams =
      entry.ungrouped.length > 0 || Object.keys(entry.groups).length > 0;

    if (!hasParams) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 px-5 text-center">
          <SlidersHorizontal className="h-7 w-7 text-muted-foreground/30" strokeWidth={1.25} />
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-muted-foreground/60">
              No parameters yet
            </p>
            <p className="text-xs text-muted-foreground/40 leading-relaxed">
              Annotate variables in the code editor to expose them here
            </p>
          </div>
          <div className="mt-1 rounded-md border border-border bg-muted/50 px-3 py-2 text-left">
            <p className="font-mono text-[11px] text-muted-foreground/50 leading-relaxed">
              <span className="text-muted-foreground/40">{'// @param'}</span>
              {'\n'}
              {'mySize = 10;'}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-muted-foreground"
            onClick={() => window.open('/playground/cheatsheet', '_blank')}
          >
            <ExternalLink className="mr-1.5 h-3 w-3" />
            Open cheatsheet
          </Button>
        </div>
      );
    }

    const px = padding === 'compact' ? 'px-3' : 'px-5';
    const py = padding === 'compact' ? 'py-2' : 'py-5';
    return (
      <ScrollArea className={`h-full ${px} ${py}`}>
        <div className="flex flex-col gap-4">
          {entry.ungrouped
            .filter((param) =>
              evaluateVisibility(param.visibilityExpr, paramValueMap),
            )
            .map((param) => (
              <div
                key={param.name}
                onMouseEnter={() => onParamHover?.(param.name)}
                onMouseLeave={() => onParamHover?.(null)}
              >
                <ParameterInput
                  param={resolveParameter(param, paramValueMap)}
                  handleCommit={handleCommit}
                  projectFiles={projectFiles}
                  onFileAdd={onFileAdd}
                  onApplySnapshot={handleApplySnapshot}
                  onSetParameter={handleSetParameter}
                  isLocked={lockedParams.has(param.name)}
                  onToggleLock={() => handleToggleLock(param.name)}
                  showLock={!productMode}
                  allParameters={parameters}
                  scadCode={scadCode}
                />
              </div>
            ))}

          {Object.keys(entry.groups).length > 0 && (
            <Accordion
              type="multiple"
              defaultValue={Object.keys(entry.groups).filter(
                (g) => !entry.collapsedGroups.has(g),
              )}
              className="w-full"
            >
              {Object.entries(entry.groups)
                .filter(([, groupParams]) =>
                  evaluateVisibility(
                    groupParams[0]?.groupVisibilityExpr,
                    paramValueMap,
                  ),
                )
                .map(([groupName, groupParams]) => (
                  <AccordionItem
                    key={groupName}
                    value={groupName}
                    className="border-border"
                  >
                    <AccordionTrigger className="py-3 text-sm font-medium text-foreground hover:no-underline">
                      {groupName}
                    </AccordionTrigger>
                      <AccordionContent className="flex flex-col gap-4 pb-4 pt-2">
                      {groupParams
                        .filter((param) =>
                          evaluateVisibility(
                            param.visibilityExpr,
                            paramValueMap,
                          ),
                        )
                        .map((param) => (
                          <div
                            key={param.name}
                            onMouseEnter={() => onParamHover?.(param.name)}
                            onMouseLeave={() => onParamHover?.(null)}
                          >
                            <ParameterInput
                              param={resolveParameter(param, paramValueMap)}
                              handleCommit={handleCommit}
                              projectFiles={projectFiles}
                              onFileAdd={onFileAdd}
                              onApplySnapshot={handleApplySnapshot}
                              onSetParameter={handleSetParameter}
                              isLocked={lockedParams.has(param.name)}
                              onToggleLock={() => handleToggleLock(param.name)}
                              showLock={!productMode}
                              allParameters={parameters}
                              scadCode={scadCode}
                            />
                          </div>
                        ))}
                    </AccordionContent>
                  </AccordionItem>
                ))}
            </Accordion>
          )}
        </div>
      </ScrollArea>
    );
  };

  /**
   * Renders either a tabbed view (when any param has a tab) or a plain
   * scroll area (backward-compatible when no tabs are used).
   */
  const renderParameterPanel = (padding: 'compact' | 'normal' = 'normal') => {
    const { tabMap, tabOrder, hasTabs, HOME_TAB } = groupedParameters;

    if (!hasTabs) {
      // No tabs — render exactly as before
      return renderTabContent(HOME_TAB, padding);
    }

    const px = padding === 'compact' ? 'px-3' : 'px-5';

    return (
      <Tabs
        defaultValue={HOME_TAB}
        className="flex flex-1 flex-col overflow-hidden"
      >
        <TabsList
          className={`shrink-0 justify-start gap-1 rounded-none border-b border-border bg-transparent ${px} h-auto pb-0`}
        >
          {tabOrder.map((tabKey) => {
            const isHome = tabKey === HOME_TAB;
            // Skip home tab if it has no content
            const entry = tabMap[tabKey];
            if (
              isHome &&
              entry.ungrouped.length === 0 &&
              Object.keys(entry.groups).length === 0
            ) {
              return null;
            }
            return (
              <TabsTrigger
                key={tabKey}
                value={tabKey}
                className="rounded-none border-b-2 border-transparent px-3 py-2 text-xs data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              >
                {isHome ? <House className="h-3.5 w-3.5" /> : tabKey}
              </TabsTrigger>
            );
          })}
        </TabsList>
        {tabOrder.map((tabKey) => (
          <TabsContent
            key={tabKey}
            value={tabKey}
            className="mt-0 flex flex-1 flex-col overflow-hidden data-[state=inactive]:hidden"
          >
            {renderTabContent(tabKey, padding)}
          </TabsContent>
        ))}
      </Tabs>
    );
  };

  // Product mode: overlay-friendly, no header/download/color sections
  if (productMode) {
    return (
      <div className="flex h-full w-full max-w-full flex-col">
        {/* Compact toolbar */}
        <div className="flex items-center justify-between px-3 py-2">
          <span className="text-sm font-medium text-foreground">
            Parameters
          </span>
          <div className="flex items-center gap-1">
            {/* Proportional scale (product mode) */}
            <Popover
              open={scalePopoverOpen}
              onOpenChange={(open) => {
                setScalePopoverOpen(open);
                if (open) {
                  captureScaleBaseline();
                  setScaleSlider(50);
                }
              }}
            >
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        disabled={!hasScalableParams}
                      >
                        <SlidersHorizontal className="h-3.5 w-3.5" />
                      </Button>
                    </PopoverTrigger>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Scale values proportionally</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <PopoverContent className="w-64 p-3" align="end">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium">Scale values</span>
                    <span className="min-w-[2.8rem] text-right text-xs tabular-nums text-muted-foreground">
                      {sliderToFactor(scaleSlider).toFixed(2)}×
                    </span>
                  </div>
                  <Slider
                    min={0}
                    max={100}
                    step={1}
                    value={[scaleSlider]}
                    onValueChange={([v]) => handleScaleChange(v)}
                    className="w-full"
                  />
                  <div className="flex justify-between text-[10px] text-muted-foreground">
                    <span>0.1×</span>
                    <span>1×</span>
                    <span>3×</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Scales all unlocked numeric values relative to their current
                    values. Locked and zero-valued parameters are unaffected.
                  </p>
                </div>
              </PopoverContent>
            </Popover>

            {/* Shuffle + strength popover (product mode) */}
            <div className="flex items-center">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 rounded-r-none"
                      disabled={parameters.length === 0}
                      onClick={() => {
                        // @pubsub params are excluded from randomization — they
                        // don't drive geometry and should only change via pub/sub.
                        const pubsubNames = new Set(
                          parameters
                            .filter((p) => p.pubsub)
                            .map((p) => p.name),
                        );
                        const extendedLocked = pubsubNames.size > 0
                          ? new Set([...lockedParams, ...pubsubNames])
                          : lockedParams;
                        const newParameters = randomizeParameters(parameters, {
                          strength: randomStrength,
                          lockedParams: extendedLocked,
                        });
                        onParametersChange(newParameters);
                      }}
                    >
                      <Shuffle className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Randomize parameters</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-5 rounded-l-none border-l border-border px-0"
                    disabled={parameters.length === 0}
                  >
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-3" align="end">
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium">
                        Randomization strength
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {Math.round(randomStrength * 100)}%
                      </span>
                    </div>
                    <Slider
                      min={0}
                      max={100}
                      step={5}
                      value={[Math.round(randomStrength * 100)]}
                      onValueChange={([v]) => setRandomStrength(v / 100)}
                      className="w-full"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      Low strength keeps values close to their current settings.
                    </p>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    disabled={parameters.length === 0}
                    onClick={() => {
                      const newParameters = parameters.map((param) => ({
                        ...param,
                        value: param.defaultValue,
                      }));
                      onParametersChange(newParameters);
                    }}
                  >
                    <RefreshCcw className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Reset all parameters</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        <ContextMenu>
          <ContextMenuTrigger asChild>
            <div className="flex flex-1 flex-col overflow-hidden">
              {renderParameterPanel('compact')}
            </div>
          </ContextMenuTrigger>
          <ContextMenuContent>
            <ContextMenuItem
              disabled={parameters.length === 0}
              onClick={handleCopyAsSnapshot}
            >
              <Clipboard className="mr-2 h-3.5 w-3.5" />
              Copy as snapshot
            </ContextMenuItem>
            <ContextMenuItem
              disabled={parameters.length === 0}
              onClick={handleDownloadSnapshotJson}
            >
              <FileJson className="mr-2 h-3.5 w-3.5" />
              Download snapshot as JSON
            </ContextMenuItem>
            {onFileAdd && (
              <ContextMenuItem
                disabled={parameters.length === 0}
                onClick={handleAddSnapshotToFiles}
              >
                <FolderDown className="mr-2 h-3.5 w-3.5" />
                Add snapshot to files
              </ContextMenuItem>
            )}
          </ContextMenuContent>
        </ContextMenu>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full max-w-full flex-col border-l border-border bg-background">
      <div className="flex h-12 items-center justify-between border-b border-border px-2">
        <div className="flex items-center gap-0.5">
          {onCollapse && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={onCollapse}
                  >
                    <PanelRightClose className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Collapse panel</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <span className="text-sm font-medium text-foreground">
            Parameters
          </span>
        </div>
        <div className="flex items-center gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={parameters.length === 0}
                  onClick={handleCreateSnapshot}
                >
                  <Camera className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Create snapshot</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={parameters.length === 0}
                  onClick={handleImportClick}
                >
                  <Upload className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Import parameters from JSON</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImportParameters}
            accept=".json"
            className="hidden"
          />

          {/* Proportional scale */}
          <Popover
            open={scalePopoverOpen}
            onOpenChange={(open) => {
              setScalePopoverOpen(open);
              if (open) {
                captureScaleBaseline();
                setScaleSlider(50);
              }
            }}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      disabled={!hasScalableParams}
                    >
                      <SlidersHorizontal className="h-3.5 w-3.5" />
                    </Button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Scale values proportionally</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <PopoverContent className="w-64 p-3" align="end">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium">Scale values</span>
                  <span className="min-w-[2.8rem] text-right text-xs tabular-nums text-muted-foreground">
                    {sliderToFactor(scaleSlider).toFixed(2)}×
                  </span>
                </div>
                <Slider
                  min={0}
                  max={100}
                  step={1}
                  value={[scaleSlider]}
                  onValueChange={([v]) => handleScaleChange(v)}
                  className="w-full"
                />
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  <span>0.1×</span>
                  <span>1×</span>
                  <span>3×</span>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Scales all unlocked numeric values relative to their current
                  values. Locked and zero-valued parameters are unaffected.
                </p>
              </div>
            </PopoverContent>
          </Popover>

          {/* Lock all / Unlock all */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={parameters.length === 0}
                  onClick={handleLockAll}
                >
                  <LockKeyhole className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Lock all parameters</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={parameters.length === 0 || lockedParams.size === 0}
                  onClick={handleUnlockAll}
                >
                  <LockKeyholeOpen className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Unlock all parameters</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Shuffle button + strength popover */}
          <div className="flex items-center">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 rounded-r-none"
                    disabled={parameters.length === 0}
                    onClick={() => {
                      // @pubsub params are excluded from randomization — they
                      // don't drive geometry and should only change via pub/sub.
                      const pubsubNames = new Set(
                        parameters
                          .filter((p) => p.pubsub)
                          .map((p) => p.name),
                      );
                      const extendedLocked = pubsubNames.size > 0
                        ? new Set([...lockedParams, ...pubsubNames])
                        : lockedParams;
                      const newParameters = randomizeParameters(parameters, {
                        strength: randomStrength,
                        lockedParams: extendedLocked,
                      });
                      onParametersChange(newParameters);
                    }}
                  >
                    <Shuffle className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Randomize parameters</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-5 rounded-l-none border-l border-border px-0"
                  disabled={parameters.length === 0}
                >
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-3" align="end">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium">
                      Randomization strength
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {Math.round(randomStrength * 100)}%
                    </span>
                  </div>
                  <Slider
                    min={0}
                    max={100}
                    step={5}
                    value={[Math.round(randomStrength * 100)]}
                    onValueChange={([v]) => setRandomStrength(v / 100)}
                    className="w-full"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Low strength keeps values close to their current settings.
                  </p>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={parameters.length === 0}
                  onClick={() => {
                    const newParameters = parameters.map((param) => ({
                      ...param,
                      value: param.defaultValue,
                    }));
                    onParametersChange(newParameters);
                  }}
                >
                  <RefreshCcw className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Reset all parameters</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {snapshots.length > 0 && (
        <div className="border-b border-border px-5 py-2">
          <Select onValueChange={handleApplySnapshot}>
            <SelectTrigger className="h-8 w-full text-xs">
              <SelectValue placeholder="Select a snapshot..." />
            </SelectTrigger>
            <SelectContent>
              {snapshots.map((snapshot) => (
                <SelectItem key={snapshot.id} value={snapshot.id}>
                  {snapshot.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex flex-1 flex-col justify-between overflow-hidden">
        <ContextMenu>
          <ContextMenuTrigger asChild>
            <div className="flex flex-1 flex-col overflow-hidden">
              {renderParameterPanel('normal')}
            </div>
          </ContextMenuTrigger>
          <ContextMenuContent>
            <ContextMenuItem
              disabled={parameters.length === 0}
              onClick={handleCopyAsSnapshot}
            >
              <Clipboard className="mr-2 h-3.5 w-3.5" />
              Copy as snapshot
            </ContextMenuItem>
            <ContextMenuItem
              disabled={parameters.length === 0}
              onClick={handleDownloadSnapshotJson}
            >
              <FileJson className="mr-2 h-3.5 w-3.5" />
              Download snapshot as JSON
            </ContextMenuItem>
            {onFileAdd && (
              <ContextMenuItem
                disabled={parameters.length === 0}
                onClick={handleAddSnapshotToFiles}
              >
                <FolderDown className="mr-2 h-3.5 w-3.5" />
                Add snapshot to files
              </ContextMenuItem>
            )}
          </ContextMenuContent>
        </ContextMenu>
        {parameters.length > 0 && (
          <div className="flex flex-col gap-4 border-t border-border px-5 py-5">
            <ModelColorSection showModelColor={coloredBlobs.length === 0} />
          </div>
        )}
        {parameters.length > 0 && (
        <div className="border-t border-border px-5 py-5">
          <div className="flex">
            <Button
              onClick={handleDownload}
              disabled={isDownloadDisabled}
              aria-label={`download ${selectedFormat.toUpperCase()} file`}
              className="h-10 flex-1 rounded-r-none"
            >
              {isExporting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {exportProgress?.message ?? 'Rendering...'}
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  {selectedFormat.toUpperCase()}
                  {selectedFormat === 'stl' && exportQuality > 1
                    ? ` · ${EXPORT_QUALITY_LABELS[exportQuality]}`
                    : ''}
                </>
              )}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  disabled={!blob && !scadCode}
                  aria-label="select download format"
                  className="h-10 w-10 rounded-l-none border-l border-primary-foreground/20 p-0"
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {([1, 2, 4, 8] as ExportQuality[]).map((q) => (
                  <DropdownMenuItem
                    key={q}
                    onClick={() => {
                      setSelectedFormat('stl');
                      setExportQuality(q);
                    }}
                    disabled={!blob}
                    className="cursor-pointer"
                  >
                    <span className="text-sm">
                      .STL {EXPORT_QUALITY_LABELS[q]}
                    </span>
                    <span className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
                      {EXPORT_QUALITY_DESCRIPTIONS[q]}
                      {selectedFormat === 'stl' && exportQuality === q && (
                        <Check className="h-3 w-3" />
                      )}
                    </span>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setSelectedFormat('scad')}
                  disabled={!scadCode}
                  className="cursor-pointer"
                >
                  <span className="text-sm">.SCAD</span>
                  <span className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
                    OpenSCAD Code
                    {selectedFormat === 'scad' && <Check className="h-3 w-3" />}
                  </span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        )}
      </div>

      <Dialog open={showSnapshotDialog} onOpenChange={setShowSnapshotDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Snapshot</DialogTitle>
            <DialogDescription>
              Enter a name for this parameter configuration.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={snapshotName}
            onChange={(e) => setSnapshotName(e.target.value)}
            placeholder="Snapshot name"
            onKeyDown={(e) => {
              if (e.key === 'Enter') confirmCreateSnapshot();
            }}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowSnapshotDialog(false)}
            >
              Cancel
            </Button>
            <Button onClick={confirmCreateSnapshot}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
